<template>
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
    <div class="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden">
      <div class="flex justify-between items-center p-6 border-b">
        <h3 class="text-lg font-semibold">좌석 예약</h3>
        <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600">
          <XMarkIcon class="w-6 h-6" />
        </button>
      </div>
      
      <div class="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
        <div class="mb-6">
          <h4 class="font-medium text-gray-900 mb-2">{{ post.title }}</h4>
          <p class="text-sm text-gray-600">
            {{ post.place }} | {{ formatDate(post.startDate) }} - {{ formatDate(post.endDate) }}
          </p>
        </div>

        <!-- 좌석 선택 -->
        <div class="mb-6">
          <h5 class="font-medium text-gray-900 mb-4">좌석을 선택해주세요</h5>
          <SeatLayout
            :key="seatLayoutKey"
            :layout="layout"
            :mode="'Reserve'"
            :reserved-seat-numbers="reservedSeatNumbers"
            :selected-seats="selectedSeats"
            :max-selectable-seats="maxSeatsPerUser"
          />
        </div>

        <!-- 선택된 좌석 정보 -->
<!--        <div v-if="selectedSeats.length > 0" class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">-->
<!--          <h5 class="font-medium text-blue-900 mb-2">선택한 좌석</h5>-->
<!--          <div class="flex flex-wrap gap-2">-->
<!--            <span-->
<!--              v-for="seatNumber in selectedSeats"-->
<!--              :key="seatNumber"-->
<!--              class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"-->
<!--            >-->
<!--              {{ seatNumber }}번-->
<!--              <button-->
<!--                @click="onSeatDeselected(seatNumber)"-->
<!--                class="ml-1 text-blue-600 hover:text-blue-800"-->
<!--              >-->
<!--                <XMarkIcon class="w-3 h-3" />-->
<!--              </button>-->
<!--            </span>-->
<!--          </div>-->
<!--        </div>-->

        <!-- 에러 메시지 -->
        <div v-if="error" class="bg-red-50 border border-red-200 rounded-md p-3 mb-6">
          <p class="text-red-600 text-sm">{{ error }}</p>
        </div>
      </div>

      <div class="p-6 border-t bg-gray-50">
        <div class="flex gap-3">
          <button
            @click="$emit('close')"
            class="btn btn-outline btn-sm flex-1"
          >
            취소
          </button>
          <button
            @click="makeReservation"
            :disabled="selectedSeats.length === 0 || isReserving"
            class="btn btn-primary btn-sm flex-1"
            :class="{ 'opacity-50 cursor-not-allowed': selectedSeats.length === 0 || isReserving }"
          >
            <span v-if="isReserving" class="flex items-center justify-center">
              <div class="spinner w-4 h-4 mr-2"></div>
              예약 중...
            </span>
            <span v-else>
              {{ selectedSeats.length > 0 ? `${selectedSeats.length}개 좌석 예약하기` : '좌석을 선택해주세요' }}
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {ref} from 'vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { reservationApi } from '@/services/api'
import type { Post, SeatLayoutInfo } from '@/types'
import { useToast } from 'vue-toastification'
import SeatLayout from '@/components/SeatLayout.vue'

interface Props {
  post: Post
  layout: SeatLayoutInfo
  reservedSeatNumbers: number[]
  maxSeatsPerUser: number
}

const props = defineProps<Props>()

const emit = defineEmits<{
  close: []
  reserved: []
}>()

const toast = useToast()

const selectedSeats = ref<number[]>([])
const isReserving = ref(false)
const error = ref('')
const seatLayoutKey = ref(0)

// 날짜 포맷
const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('ko-KR')
}

// 좌석 선택 처리
// 예약 실행
const makeReservation = async () => {
  if (selectedSeats.value.length === 0) {
    error.value = '예약할 좌석을 선택해주세요.'
    return
  }

  try {
    isReserving.value = true
    error.value = ''
    
    await reservationApi.createReservation({
      postId: props.post.id,
      seatNumbers: selectedSeats.value
    })

    toast.success('좌석이 예약되었습니다!')
    selectedSeats.value = []
    seatLayoutKey.value++
    emit('reserved')
  } catch (error: any) {
    console.error('Failed to make reservation:', error)
    if (error.response?.data?.message) {
      error.value = error.response.data.message
    } else if (error.response?.status === 409) {
      error.value = '이미 예약된 좌석입니다. 다른 좌석을 선택해주세요.'
    } else if (error.response?.status === 400) {
      error.value = '잘못된 요청입니다. 다시 시도해주세요.'
    } else {
      error.value = '예약에 실패했습니다. 다시 시도해주세요.'
    }
  } finally {
    isReserving.value = false
  }
}
</script>
